import { useEffect, useMemo, useRef, useState } from "react";
import { L } from "@/i18n/routing";
import { useDict } from "@/i18n/useDict";
import { moduleRegistry, categoryOrder } from "./modules";
import LanguageSwitcher from "./LanguageSwitcher";

const Navbar = () => {
  const { dict } = useDict();
  const [open, setOpen] = useState(false);
  const [modulesOpen, setModulesOpen] = useState(false);
  const [mobileModulesOpen, setMobileModulesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!modulesOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setModulesOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModulesOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [modulesOpen]);

  const moduleHref = (slug: string) => `/module/${slug}`;

  const groups = useMemo(
    () =>
      categoryOrder.map((category) => ({
        category,
        label: dict.moduleNav.categories[category],
        items: moduleRegistry.filter((m) => m.category === category),
      })),
    [dict]
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <L to="/" className="flex items-center">
          <img src="/optify-wordmark-ink.svg" alt="Optify" className="h-7" />
        </L>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setModulesOpen((v) => !v)}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              aria-expanded={modulesOpen}
              aria-haspopup="true"
            >
              {dict.nav.modules}
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform ${modulesOpen ? "rotate-180" : ""}`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {modulesOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[720px] rounded-2xl border border-border bg-card shadow-xl p-6 grid grid-cols-3 gap-6">
                {groups.map((group) => (
                  <div key={group.category}>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
                      {group.label}
                    </p>
                    <ul className="space-y-1.5">
                      {group.items.map((m) => (
                        <li key={m.slug}>
                          <L
                            to={moduleHref(m.slug)}
                            onClick={() => setModulesOpen(false)}
                            className="block text-sm text-foreground hover:text-primary transition-colors py-1"
                          >
                            {dict.moduleNav.titles[m.slug as keyof typeof dict.moduleNav.titles]}
                          </L>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <L to="/#preturi" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{dict.nav.pricing}</L>
          <L to="/#studiu" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{dict.nav.caseStudy}</L>
          <L to="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{dict.blog.navLabel}</L>
          <L to="/#contact" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{dict.nav.contact}</L>
          <LanguageSwitcher variant="desktop" />
          <L to="/#contact" className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
            {dict.nav.requestDemo}
          </L>
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher variant="mobile" />
          <button onClick={() => setOpen(!open)} className="p-2 text-foreground" aria-label={dict.nav.openMenu}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {open ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></> : <><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></>}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="mobile-menu-holder md:hidden bg-background border-b border-border px-6 pb-4 flex flex-col gap-1">
          <button
            onClick={() => setMobileModulesOpen((v) => !v)}
            className="flex items-center justify-between text-sm font-medium text-muted-foreground py-2"
          >
            {dict.nav.modules}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform ${mobileModulesOpen ? "rotate-180" : ""}`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {mobileModulesOpen && (
            <div className="pl-3 pb-2 flex flex-col gap-3 border-l border-border">
              {groups.map((group) => (
                <div key={group.category}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 mt-1">
                    {group.label}
                  </p>
                  <ul className="space-y-1">
                    {group.items.map((m) => (
                      <li key={m.slug}>
                        <L
                          to={moduleHref(m.slug)}
                          onClick={() => {
                            setOpen(false);
                            setMobileModulesOpen(false);
                          }}
                          className="block text-sm text-foreground py-1"
                        >
                          {dict.moduleNav.titles[m.slug as keyof typeof dict.moduleNav.titles]}
                        </L>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <L to="/#preturi" onClick={() => setOpen(false)} className="text-sm font-medium text-muted-foreground py-2">{dict.nav.pricing}</L>
          <L to="/#studiu" onClick={() => setOpen(false)} className="text-sm font-medium text-muted-foreground py-2">{dict.nav.caseStudy}</L>
          <L to="/blog" onClick={() => setOpen(false)} className="text-sm font-medium text-muted-foreground py-2">{dict.blog.navLabel}</L>
          <L to="/#contact" onClick={() => setOpen(false)} className="text-sm font-medium text-muted-foreground py-2">{dict.nav.contact}</L>
          <L to="/#contact" onClick={() => setOpen(false)} className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold mt-1">
            {dict.nav.requestDemo}
          </L>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
